import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import { generateAndDownloadTenderPackage, generateDefaultQuotations, downloadPDF, generateTenderPDF, generateQuotationPDF } from '../../utils/pdfGenerator';
import Layout from '../../components/Layout';

const AdminRequisition = ({ onNavigate, userInfo }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [selectedTender, setSelectedTender] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [error, setError] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Admin data
  const [adminData] = useState({
    name: userInfo?.name || "Administrator",
    adminId: userInfo?.id || "ADM000001",
    designation: "System Administrator",
    department: "Administration"
  });

  // Dashboard Statistics
  const [dashboardStats, setDashboardStats] = useState({
    totalRequisitions: 0,
    pendingFinalApproval: 0,
    activeTenders: 0,
    registeredVendors: 0,
    completedProcurements: 0,
    totalBudgetValue: 0,
    costSavings: 0,
    avgProcessingTime: 0
  });

  // Data states
  const [pendingRequisitions, setPendingRequisitions] = useState([]);
  const [activeTenders, setActiveTenders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [bids, setBids] = useState([]);
  const [categories, setCategories] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [tenderAssignments, setTenderAssignments] = useState([]);

  // Form states
  const [newVendor, setNewVendor] = useState({
    vendor_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    state: '',
    pincode: '',
    gst_number: '',
    pan_number: '',
    company_type: 'private_limited',
    specialization_categories: [],
    performance_rating: 0
  });

  const [newTender, setNewTender] = useState({
    title: '',
    description: '',
    estimated_value: '',
    department: '',
    procurement_category: '',
    submission_deadline: '',
    tender_type: 'open'
  });

  const [vendorAssignment, setVendorAssignment] = useState({
    tender_id: '',
    vendor_ids: [],
    assignment_date: '',
    notes: ''
  });

  // Check mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch data on component mount and set up refresh interval
  useEffect(() => {
    fetchAllData();
    // Refresh data every 30 seconds for real-time updates
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
  }, []);

  // API helper function
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        handleLogout();
        throw new Error('Session expired');
      }
      throw new Error(`API error: ${response.status}`);
    }

    return response.json();
  };

  // Fetch all procurement data
  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Fetching all procurement data...');

      // Fetch dashboard stats
      try {
        const dashboardResponse = await apiCall('/procurement/dashboard');
        if (dashboardResponse.success) {
          setDashboardStats(dashboardResponse.data);
          console.log('✅ Dashboard stats loaded');
        }
      } catch (err) {
        console.log('Dashboard API not available, using calculated stats');
        // Calculate stats from existing data
        setDashboardStats(prev => ({
          ...prev,
          totalRequisitions: pendingRequisitions.length,
          pendingFinalApproval: pendingRequisitions.filter(req =>
            ['admin_pending', 'hod_approved', 'forwarded_to_admin', 'pending_admin_approval'].includes(req.status)
          ).length,
          activeTenders: activeTenders.length,
          registeredVendors: vendors.length,
          completedProcurements: workOrders.filter(wo => wo.order_status === 'completed').length,
          totalBudgetValue: 10000000,
          costSavings: 150000,
          avgProcessingTime: 7
        }));
      }

      // Fetch requisitions
      try {
        const requisitionsResponse = await apiCall('/procurement/requisitions');
        if (requisitionsResponse.success && requisitionsResponse.data.requisitions) {
          const allReqs = requisitionsResponse.data.requisitions;
          const pending = allReqs.filter(req =>
            ['admin_pending', 'hod_approved', 'forwarded_to_admin', 'pending_admin_approval'].includes(req.status)
          );
          setPendingRequisitions(pending);
          console.log('✅ Requisitions loaded:', pending.length);
        }
      } catch (err) {
        console.log('Requisitions API not available');
        setPendingRequisitions([]);
      }

      // Fetch tenders
      try {
        const tendersResponse = await apiCall('/procurement/tenders');
        if (tendersResponse.success && tendersResponse.data.tenders) {
          setActiveTenders(tendersResponse.data.tenders);
          console.log('✅ Tenders loaded:', tendersResponse.data.tenders.length);
        }
      } catch (err) {
        console.log('Tenders API not available');
        setActiveTenders([]);
      }

      // Fetch vendors
      try {
        const vendorsResponse = await apiCall('/procurement/vendors');
        if (vendorsResponse.success && vendorsResponse.data.vendors) {
          setVendors(vendorsResponse.data.vendors);
          console.log('✅ Vendors loaded:', vendorsResponse.data.vendors.length);
        }
      } catch (err) {
        console.log('Vendors API not available');
        setVendors([]);
      }

      // Fetch work orders
      try {
        const workOrdersResponse = await apiCall('/procurement/work-orders');
        if (workOrdersResponse.success && workOrdersResponse.data.workOrders) {
          setWorkOrders(workOrdersResponse.data.workOrders);
          console.log('✅ Work orders loaded:', workOrdersResponse.data.workOrders.length);
        }
      } catch (err) {
        console.log('Work orders API not available');
        setWorkOrders([]);
      }

      // Fetch bids
      try {
        const bidsResponse = await apiCall('/procurement/bids');
        if (bidsResponse.success && bidsResponse.data.bids) {
          setBids(bidsResponse.data.bids);
          console.log('✅ Bids loaded:', bidsResponse.data.bids.length);
        }
      } catch (err) {
        console.log('Bids API not available');
        setBids([]);
      }

      // Fetch categories
      try {
        const categoriesResponse = await apiCall('/procurement/categories');
        if (categoriesResponse.success && categoriesResponse.data.categories) {
          setCategories(categoriesResponse.data.categories);
          console.log('✅ Categories loaded:', categoriesResponse.data.categories.length);
        }
      } catch (err) {
        console.log('Categories API not available, using default');
        setCategories([
          { category_code: 'equipment', category_name: 'Equipment & Technology', category_icon: '💻' },
          { category_code: 'stationery', category_name: 'Stationery & Office Supplies', category_icon: '📚' },
          { category_code: 'maintenance', category_name: 'Maintenance & Repair', category_icon: '🔧' },
          { category_code: 'books', category_name: 'Books & References', category_icon: '📖' },
          { category_code: 'furniture', category_name: 'Furniture & Fixtures', category_icon: '🪑' }
        ]);
      }

      // Fetch quotations
      try {
        const quotationsResponse = await apiCall('/procurement/quotations');
        if (quotationsResponse.success && quotationsResponse.data.quotations) {
          setQuotations(quotationsResponse.data.quotations);
          console.log('✅ Quotations loaded:', quotationsResponse.data.quotations.length);
        }
      } catch (err) {
        console.log('Quotations API not available');
        setQuotations([]);
      }

      // Fetch tender assignments
      try {
        const assignmentsResponse = await apiCall('/procurement/tender-assignments');
        if (assignmentsResponse.success && assignmentsResponse.data.assignments) {
          setTenderAssignments(assignmentsResponse.data.assignments);
          console.log('✅ Tender assignments loaded:', assignmentsResponse.data.assignments.length);
        }
      } catch (err) {
        console.log('Tender assignments API not available');
        setTenderAssignments([]);
      }

      console.log('🎉 All data fetching completed!');

    } catch (error) {
      console.error('❌ Error fetching procurement data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle vendor registration
  const handleVendorRegistration = async () => {
    try {
      setLoading(true);
      console.log('Registering vendor:', newVendor);

      // Validate required fields
      if (!newVendor.vendor_name || !newVendor.contact_person || !newVendor.email ||
        !newVendor.phone || !newVendor.address_line1 || !newVendor.city ||
        !newVendor.state || !newVendor.pincode) {
        alert('❌ Please fill all required fields');
        return;
      }

      const response = await apiCall('/procurement/vendors', {
        method: 'POST',
        body: JSON.stringify(newVendor)
      });

      if (response.success) {
        await fetchAllData();
        setShowModal(false);
        setNewVendor({
          vendor_name: '',
          contact_person: '',
          email: '',
          phone: '',
          address_line1: '',
          city: '',
          state: '',
          pincode: '',
          gst_number: '',
          pan_number: '',
          company_type: 'private_limited',
          specialization_categories: [],
          performance_rating: 0
        });
        alert('✅ Vendor registered successfully!');
      } else {
        throw new Error(response.message || 'Failed to register vendor');
      }
    } catch (error) {
      console.error('Error registering vendor:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle tender creation
  const handleTenderCreation = async () => {
    try {
      setLoading(true);
      console.log('Creating tender:', newTender);

      // Validate required fields
      if (!newTender.title || !newTender.description || !newTender.estimated_value ||
        !newTender.department || !newTender.procurement_category || !newTender.submission_deadline) {
        alert('❌ Please fill all required fields');
        return;
      }

      // Add tender ID for consistency
      const tenderWithId = {
        ...newTender,
        tender_id: `TND-${Date.now()}`,
        created_at: new Date().toISOString(),
        tender_status: 'published'
      };

      const response = await apiCall('/procurement/tenders', {
        method: 'POST',
        body: JSON.stringify(tenderWithId)
      });

      if (response.success) {
        // Generate 3 quotations automatically
        console.log('🔄 Generating quotations and PDFs...');
        const generatedQuotations = generateDefaultQuotations(tenderWithId);

        // Save quotations to database
        try {
          for (const quotation of generatedQuotations) {
            await apiCall('/procurement/quotations', {
              method: 'POST',
              body: JSON.stringify(quotation)
            });
          }
          console.log('✅ Quotations saved to database');
        } catch (quotationError) {
          console.log('⚠️ Could not save quotations to database:', quotationError.message);
        }

        // Generate and download PDF package
        const pdfResult = generateAndDownloadTenderPackage(tenderWithId);

        if (pdfResult.success) {
          alert('✅ Tender created successfully!\n📄 PDFs are being downloaded automatically.\n📋 3 quotations generated.');
        } else {
          alert('✅ Tender created successfully!\n⚠️ PDF generation failed: ' + pdfResult.error);
        }

        await fetchAllData();
        setShowModal(false);
        setNewTender({
          title: '',
          description: '',
          estimated_value: '',
          department: '',
          procurement_category: '',
          submission_deadline: '',
          tender_type: 'open'
        });
      } else {
        throw new Error(response.message || 'Failed to create tender');
      }
    } catch (error) {
      console.error('Error creating tender:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle vendor blacklisting
  const handleVendorBlacklist = async (vendorId, reason) => {
    try {
      setLoading(true);
      console.log('Blacklisting vendor:', vendorId, 'Reason:', reason);

      const response = await apiCall(`/procurement/vendors/${vendorId}/blacklist`, {
        method: 'POST',
        body: JSON.stringify({ reason })
      });

      if (response.success) {
        await fetchAllData();
        alert('✅ Vendor blacklisted successfully!');
      } else {
        throw new Error(response.message || 'Failed to blacklist vendor');
      }
    } catch (error) {
      console.error('Error blacklisting vendor:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle vendor assignment to tender
  const handleVendorAssignment = async () => {
    try {
      setLoading(true);
      console.log('Assigning vendors to tender:', vendorAssignment);

      // Validate required fields
      if (!vendorAssignment.tender_id || vendorAssignment.vendor_ids.length === 0) {
        alert('❌ Please select a tender and at least one vendor');
        return;
      }

      const assignmentData = {
        ...vendorAssignment,
        assignment_date: vendorAssignment.assignment_date || new Date().toISOString(),
        created_at: new Date().toISOString(),
        status: 'assigned'
      };

      const response = await apiCall('/procurement/tender-assignments', {
        method: 'POST',
        body: JSON.stringify(assignmentData)
      });

      if (response.success) {
        await fetchAllData();
        setShowModal(false);
        setVendorAssignment({
          tender_id: '',
          vendor_ids: [],
          assignment_date: '',
          notes: ''
        });
        alert('✅ Vendors assigned to tender successfully!');
      } else {
        throw new Error(response.message || 'Failed to assign vendors');
      }
    } catch (error) {
      console.error('Error assigning vendors:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Download quotation PDF
  const handleDownloadQuotation = (quotation, tender) => {
    try {
      const quotationPDF = generateQuotationPDF(quotation, tender);
      downloadPDF(quotationPDF, `Quotation_${quotation.quotation_id}`);
      alert('📄 Quotation PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading quotation:', error);
      alert('❌ Error downloading quotation PDF');
    }
  };

  // Download tender PDF with quotations
  const handleDownloadTenderPackage = (tender) => {
    try {
      const tenderQuotations = quotations.filter(q => q.tender_id === tender.tender_id);
      const result = generateAndDownloadTenderPackage({ ...tender, quotations: tenderQuotations });

      if (result.success) {
        alert('📄 Tender package downloaded successfully!');
      } else {
        alert('❌ Error downloading tender package: ' + result.error);
      }
    } catch (error) {
      console.error('Error downloading tender package:', error);
      alert('❌ Error downloading tender package');
    }
  };

  // Handle final approval
  const handleFinalApproval = async (requisitionId, action, comment = '') => {
    try {
      setLoading(true);

      const endpoint = `/procurement/requisitions/${requisitionId}/${action}`;
      const body = action === 'reject' ? { comment } : {};

      const response = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      if (response.success) {
        await fetchAllData();

        if (action === 'approve') {
          alert('✅ Requisition approved successfully!');
        } else {
          alert('✅ Requisition rejected with feedback sent to department.');
        }
        setShowModal(false);
        setSelectedRequisition(null);
        setRejectionReason('');
      } else {
        throw new Error(response.message || 'Failed to process approval');
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle logout (kept for apiCall 401 handling)
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      fetch(`${API_LINK}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }).catch(error => console.error('Logout API error:', error));
    }

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': '#ffc107',
      'approved': '#28a745',
      'rejected': '#dc3545',
      'published': '#17a2b8',
      'draft': '#6c757d',
      'admin_pending': '#ffc107',
      'hod_approved': '#17a2b8',
      'forwarded_to_admin': '#ffc107',
      'pending_admin_approval': '#ffc107',
      'verified': '#28a745',
      'blacklisted': '#dc3545',
      'registered': '#6c757d',
      'active': '#28a745'
    };
    return colors[status] || '#6c757d';
  };

  // Export to Excel functionality
  const exportToExcel = (type, data) => {
    try {
      let csvContent = '';

      switch (type) {
        case 'vendors':
          csvContent = 'Vendor Name,Contact Person,Email,Phone,Status,Performance Rating\n';
          data.forEach(vendor => {
            csvContent += `"${vendor.vendor_name}","${vendor.contact_person}","${vendor.email}","${vendor.phone}","${vendor.vendor_status}","${vendor.performance_rating}"\n`;
          });
          break;
        case 'tenders':
          csvContent = 'Title,Department,Estimated Value,Status,Submission Deadline\n';
          data.forEach(tender => {
            csvContent += `"${tender.title}","${tender.department}","${tender.estimated_value}","${tender.tender_status}","${tender.submission_deadline}"\n`;
          });
          break;
        case 'requisitions':
          csvContent = 'Title,Department,Requested By,Estimated Cost,Status\n';
          data.forEach(req => {
            csvContent += `"${req.title}","${req.department}","${req.requester_name}","${req.estimated_cost}","${req.status}"\n`;
          });
          break;
        default:
          csvContent = 'No data available\n';
      }

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${type}_report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert(`📊 ${type} report exported successfully!`);
    } catch (error) {
      console.error('Export error:', error);
      alert('❌ Error exporting data');
    }
  };

  // Loading state
  if (loading && (pendingRequisitions.length === 0 && activeTenders.length === 0 && vendors.length === 0)) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Procurement Portal...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <Layout role="admin" onNavigate={onNavigate} activePage="admin-requisition" userInfo={userInfo}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          .tab-content {
            animation: fadeIn 0.3s ease-in-out;
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      {/* Header Section */}
      <section style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        borderLeft: '4px solid #238548'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '1rem'
        }}>
          <div>
            <h2 style={{
              color: '#238548',
              margin: '0 0 0.5rem 0',
              fontSize: '2rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🏭 Advanced Procurement Management
            </h2>
            <p style={{ color: '#666', margin: 0, fontSize: '1.1rem' }}>
              Complete e-procurement system with tender management, vendor portal & analytics
            </p>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => exportToExcel('procurement-report', [])}
              style={{
                background: '#28a745',
                color: '#ffffff',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#218838';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#28a745';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              📊 Export Report
            </button>
            <button
              onClick={fetchAllData}
              style={{
                background: '#17a2b8',
                color: '#ffffff',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#138496';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#17a2b8';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              🔄 Refresh Data
            </button>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section style={{
        background: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e9ecef',
          overflowX: 'auto'
        }}>
          {[
            { key: 'dashboard', label: 'Dashboard', icon: '📊' },
            { key: 'requisition-approval', label: 'Final Approvals', icon: '✅' },
            { key: 'tender-management', label: 'Tender Management', icon: '📋' },
            { key: 'vendor-management', label: 'Vendor Management', icon: '🏢' },
            { key: 'bid-evaluation', label: 'Bid Evaluation', icon: '🔍' },
            { key: 'work-orders', label: 'Work Orders', icon: '📄' },
            { key: 'analytics', label: 'Analytics & Reports', icon: '📈' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                padding: '1.2rem 1rem',
                border: 'none',
                background: activeTab === tab.key ? '#238548' : 'transparent',
                color: activeTab === tab.key ? '#ffffff' : '#666',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                minWidth: '140px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.key) {
                  e.target.style.backgroundColor = '#f8f9fa';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.key) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding: '2rem' }} className="tab-content">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div>
              <h3 style={{ margin: '0 0 2rem 0', color: '#333', fontSize: '1.5rem' }}>
                📊 Procurement Dashboard Overview
              </h3>

              {/* Key Metrics */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                gap: '1.5rem',
                marginBottom: '2rem'
              }}>
                {[
                  {
                    label: 'Total Budget Value',
                    value: formatCurrency(dashboardStats.totalBudgetValue),
                    color: '#238548',
                    icon: '💰',
                    change: '+12.5%'
                  },
                  {
                    label: 'Active Tenders',
                    value: activeTenders.length,
                    color: '#17a2b8',
                    icon: '📋',
                    change: `+${activeTenders.length}`
                  },
                  {
                    label: 'Registered Vendors',
                    value: vendors.length,
                    color: '#28a745',
                    icon: '🏢',
                    change: `+${vendors.length}`
                  },
                  {
                    label: 'Pending Approvals',
                    value: pendingRequisitions.length,
                    color: '#ffc107',
                    icon: '⏱️',
                    change: `${pendingRequisitions.length} pending`
                  }
                ].map((metric, index) => (
                  <div key={index} style={{
                    background: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    border: '1px solid #e9ecef',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}>
                    <div style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      background: `${metric.color}20`,
                      color: metric.color,
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      fontWeight: 'bold'
                    }}>
                      {metric.change}
                    </div>
                    <div style={{ fontSize: '2.5rem', color: metric.color, marginBottom: '0.5rem' }}>
                      {metric.icon}
                    </div>
                    <h4 style={{ margin: '0 0 0.25rem 0', color: '#333', fontSize: '1rem' }}>
                      {metric.label}
                    </h4>
                    <p style={{ fontSize: '1.8rem', fontWeight: 'bold', color: metric.color, margin: 0 }}>
                      {metric.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div style={{
                background: '#f8f9fa',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #e9ecef'
              }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>⚡ Quick Actions</h4>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '0.75rem' }}>
                  {[
                    { label: 'Pending Final Approvals', count: pendingRequisitions.length, action: () => setActiveTab('requisition-approval'), color: '#ffc107' },
                    { label: 'Active Tenders', count: activeTenders.length, action: () => setActiveTab('tender-management'), color: '#17a2b8' },
                    { label: 'Total Vendors', count: vendors.length, action: () => setActiveTab('vendor-management'), color: '#6f42c1' },
                    { label: 'Work Orders', count: workOrders.length, action: () => setActiveTab('work-orders'), color: '#28a745' }
                  ].map((action, index) => (
                    <button
                      key={index}
                      onClick={action.action}
                      style={{
                        background: '#ffffff',
                        border: `1px solid ${action.color}`,
                        padding: '1rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        textAlign: 'left',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = `${action.color}10`;
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#ffffff';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      <span style={{ color: '#333', fontWeight: '500', fontSize: '0.9rem' }}>{action.label}</span>
                      <span style={{
                        color: action.color,
                        fontSize: '1.5rem',
                        fontWeight: 'bold'
                      }}>
                        {action.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Final Approvals Tab */}
          {activeTab === 'requisition-approval' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <h3 style={{ margin: 0, color: '#333', fontSize: '1.5rem' }}>
                  ✅ Requisitions Awaiting Final Approval ({pendingRequisitions.length})
                </h3>
                <button
                  onClick={() => exportToExcel('requisitions', pendingRequisitions)}
                  style={{
                    background: '#28a745',
                    color: '#ffffff',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📊 Export to Excel
                </button>
              </div>

              {pendingRequisitions.length > 0 ? (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {pendingRequisitions.map((req) => (
                    <div key={req.requisition_id} style={{
                      background: '#ffffff',
                      border: '1px solid #e9ecef',
                      borderRadius: '12px',
                      padding: '2rem',
                      transition: 'all 0.3s ease'
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1.5rem',
                        flexWrap: 'wrap',
                        gap: '1rem'
                      }}>
                        <div style={{ flex: 1, minWidth: '300px' }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            marginBottom: '1rem',
                            flexWrap: 'wrap'
                          }}>
                            <h4 style={{ margin: 0, color: '#333', fontSize: '1.3rem' }}>
                              {req.title || 'Untitled Requisition'}
                            </h4>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              fontWeight: 'bold',
                              backgroundColor: `${getStatusColor(req.priority || 'medium')}20`,
                              color: getStatusColor(req.priority || 'medium'),
                              textTransform: 'uppercase'
                            }}>
                              {req.priority || 'medium'} Priority
                            </span>
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.8rem',
                              fontWeight: 'bold',
                              backgroundColor: `${getStatusColor(req.status)}20`,
                              color: getStatusColor(req.status),
                              textTransform: 'uppercase'
                            }}>
                              {req.status}
                            </span>
                          </div>

                          <p style={{ color: '#666', margin: '0 0 1rem 0', lineHeight: 1.6 }}>
                            {req.description || 'No description provided'}
                          </p>

                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                            gap: '0.75rem',
                            fontSize: '0.9rem',
                            color: '#666'
                          }}>
                            <div><strong>Requisition ID:</strong> {req.requisition_id}</div>
                            <div><strong>Department:</strong> {req.department || 'N/A'}</div>
                            <div><strong>Requested By:</strong> {req.requester_name || req.requested_by || 'N/A'}</div>
                            <div><strong>Estimated Cost:</strong> <span style={{ color: '#238548', fontWeight: 'bold' }}>{formatCurrency(req.estimated_cost)}</span></div>
                            <div><strong>Submitted:</strong> {formatDate(req.submitted_at || req.created_at)}</div>
                            <div><strong>HOD Approved:</strong> {formatDate(req.hod_approved_at)} {req.hod_approved_by ? `by ${req.hod_approved_by}` : ''}</div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'flex-end' }}>
                          <button
                            onClick={() => {
                              setSelectedRequisition(req);
                              setModalType('approve');
                              setShowModal(true);
                            }}
                            style={{
                              background: '#28a745',
                              color: '#ffffff',
                              border: 'none',
                              padding: '0.75rem 1.5rem',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              transition: 'all 0.3s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#218838';
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#28a745';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            ✅ Final Approve
                          </button>

                          <button
                            onClick={() => {
                              setSelectedRequisition(req);
                              setModalType('reject');
                              setShowModal(true);
                            }}
                            style={{
                              background: '#dc3545',
                              color: '#ffffff',
                              border: 'none',
                              padding: '0.75rem 1.5rem',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              transition: 'all 0.3s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#c82333';
                              e.target.style.transform = 'translateY(-2px)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = '#dc3545';
                              e.target.style.transform = 'translateY(0)';
                            }}
                          >
                            ❌ Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem 2rem',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>✅</div>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>No Pending Approvals</h4>
                  <p style={{ margin: 0, fontSize: '1rem' }}>
                    All requisitions have been processed. New submissions will appear here.
                  </p>
                  <button
                    onClick={fetchAllData}
                    style={{
                      background: '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      marginTop: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    🔄 Refresh Data
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tender Management Tab */}
          {activeTab === 'tender-management' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <h3 style={{ margin: 0, color: '#333', fontSize: '1.5rem' }}>
                  📋 Tender Management ({activeTenders.length})
                </h3>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      setModalType('create-tender');
                      setShowModal(true);
                    }}
                    style={{
                      background: '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    ➕ Create New Tender
                  </button>
                  <button
                    onClick={() => {
                      setModalType('assign-vendor');
                      setShowModal(true);
                    }}
                    style={{
                      background: '#6f42c1',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    👥 Assign Vendors
                  </button>
                  <button
                    onClick={() => exportToExcel('tenders', activeTenders)}
                    style={{
                      background: '#28a745',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    📊 Export to Excel
                  </button>
                </div>
              </div>

              {activeTenders.length > 0 ? (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {activeTenders.map((tender) => {
                    const tenderQuotations = quotations.filter(q => q.tender_id === tender.tender_id);
                    const assignedVendors = tenderAssignments.filter(assignment => assignment.tender_id === tender.tender_id);

                    return (
                      <div key={tender.tender_id} style={{
                        background: '#ffffff',
                        border: '1px solid #e9ecef',
                        borderRadius: '12px',
                        padding: '2rem',
                        transition: 'all 0.3s ease'
                      }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = 'none';
                        }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '1rem',
                          flexWrap: 'wrap',
                          gap: '1rem'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                              <h4 style={{ margin: 0, color: '#333', fontSize: '1.3rem' }}>
                                {tender.title}
                              </h4>
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                borderRadius: '20px',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                backgroundColor: `${getStatusColor(tender.tender_status)}20`,
                                color: getStatusColor(tender.tender_status),
                                textTransform: 'uppercase'
                              }}>
                                {tender.tender_status}
                              </span>
                            </div>
                            <p style={{ color: '#666', margin: '0 0 1rem 0' }}>
                              {tender.description}
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
                              <div><strong>Tender ID:</strong> {tender.tender_id}</div>
                              <div><strong>Department:</strong> {tender.department}</div>
                              <div><strong>Est. Value:</strong> {formatCurrency(tender.estimated_value)}</div>
                              <div><strong>Deadline:</strong> {formatDate(tender.submission_deadline)}</div>
                              <div><strong>Quotations:</strong> {tenderQuotations.length}/3 generated</div>
                              <div><strong>Assigned Vendors:</strong> {assignedVendors.length > 0 ? `${assignedVendors[0].vendor_ids?.length || 0} vendors` : 'None'}</div>
                            </div>

                            {/* Quotations Section */}
                            {tenderQuotations.length > 0 && (
                              <div style={{ marginTop: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
                                <h5 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>📋 Generated Quotations:</h5>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '0.5rem' }}>
                                  {tenderQuotations.map((quotation, index) => (
                                    <div key={quotation.quotation_id} style={{
                                      background: '#ffffff',
                                      padding: '0.75rem',
                                      borderRadius: '6px',
                                      border: '1px solid #e9ecef',
                                      fontSize: '0.85rem'
                                    }}>
                                      <div style={{ fontWeight: 'bold', color: '#238548' }}>{quotation.type}</div>
                                      <div style={{ color: '#666' }}>Total: {formatCurrency(quotation.grandTotal)}</div>
                                      <button
                                        onClick={() => handleDownloadQuotation(quotation, tender)}
                                        style={{
                                          background: '#17a2b8',
                                          color: '#ffffff',
                                          border: 'none',
                                          padding: '0.25rem 0.5rem',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontSize: '0.75rem',
                                          marginTop: '0.25rem'
                                        }}
                                      >
                                        📄 Download
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                            <button
                              onClick={() => handleDownloadTenderPackage(tender)}
                              style={{
                                background: '#17a2b8',
                                color: '#ffffff',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              📦 Download Package
                            </button>
                            <button
                              onClick={() => {
                                setVendorAssignment({ ...vendorAssignment, tender_id: tender.tender_id });
                                setModalType('assign-vendor');
                                setShowModal(true);
                              }}
                              style={{
                                background: '#6f42c1',
                                color: '#ffffff',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              👥 Assign Vendors
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem 2rem',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>📋</div>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>No Active Tenders</h4>
                  <p style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>
                    Create your first tender to start the procurement process.
                  </p>
                  <button
                    onClick={() => {
                      setModalType('create-tender');
                      setShowModal(true);
                    }}
                    style={{
                      background: '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    ➕ Create First Tender
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Vendor Management Tab */}
          {activeTab === 'vendor-management' && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <h3 style={{ margin: 0, color: '#333', fontSize: '1.5rem' }}>
                  🏢 Vendor Management ({vendors.length})
                </h3>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      setModalType('register-vendor');
                      setShowModal(true);
                    }}
                    style={{
                      background: '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    ➕ Register New Vendor
                  </button>
                  <button
                    onClick={() => exportToExcel('vendors', vendors)}
                    style={{
                      background: '#28a745',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    📊 Export to Excel
                  </button>
                </div>
              </div>

              {vendors.length > 0 ? (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {vendors.map((vendor) => (
                    <div key={vendor.vendor_id} style={{
                      background: '#ffffff',
                      border: '1px solid #e9ecef',
                      borderRadius: '12px',
                      padding: '2rem',
                      transition: 'all 0.3s ease'
                    }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '1rem',
                        flexWrap: 'wrap',
                        gap: '1rem'
                      }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.3rem' }}>
                            {vendor.vendor_name}
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <div><strong>Vendor ID:</strong> {vendor.vendor_id}</div>
                            <div><strong>Contact Person:</strong> {vendor.contact_person}</div>
                            <div><strong>Email:</strong> {vendor.email}</div>
                            <div><strong>Phone:</strong> {vendor.phone}</div>
                            <div><strong>Company Type:</strong> {vendor.company_type}</div>
                            <div><strong>Performance:</strong> ⭐ {vendor.performance_rating || 0}/5</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                          <span style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: 'bold',
                            backgroundColor: `${getStatusColor(vendor.vendor_status)}20`,
                            color: getStatusColor(vendor.vendor_status),
                            textTransform: 'uppercase'
                          }}>
                            {vendor.vendor_status}
                          </span>
                          {vendor.vendor_status !== 'blacklisted' && (
                            <button
                              onClick={() => {
                                const reason = prompt('Enter blacklisting reason:');
                                if (reason) {
                                  handleVendorBlacklist(vendor.vendor_id, reason);
                                }
                              }}
                              style={{
                                background: '#dc3545',
                                color: '#ffffff',
                                border: 'none',
                                padding: '0.5rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                transition: 'all 0.3s ease'
                              }}
                            >
                              🚫 Blacklist
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem 2rem',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>🏢</div>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>No Vendors Registered</h4>
                  <p style={{ margin: '0 0 1rem 0', fontSize: '1rem' }}>
                    Register your first vendor to start building your supplier network.
                  </p>
                  <button
                    onClick={() => {
                      setModalType('register-vendor');
                      setShowModal(true);
                    }}
                    style={{
                      background: '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    ➕ Register First Vendor
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Bid Evaluation Tab */}
          {activeTab === 'bid-evaluation' && (
            <div>
              <h3 style={{ margin: '0 0 2rem 0', color: '#333', fontSize: '1.5rem' }}>
                🔍 Bid Evaluation ({bids.length})
              </h3>

              {bids.length > 0 ? (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {bids.map((bid) => (
                    <div key={bid.bid_id} style={{
                      background: '#ffffff',
                      border: '1px solid #e9ecef',
                      borderRadius: '12px',
                      padding: '2rem',
                      transition: 'all 0.3s ease'
                    }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>
                        Bid ID: {bid.bid_id}
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
                        <div><strong>Tender:</strong> {bid.tender_id}</div>
                        <div><strong>Vendor:</strong> {bid.vendor_id}</div>
                        <div><strong>Amount:</strong> {formatCurrency(bid.quoted_amount)}</div>
                        <div><strong>Status:</strong> <span style={{ color: getStatusColor(bid.bid_status) }}>{bid.bid_status}</span></div>
                        <div><strong>Submitted:</strong> {formatDate(bid.submission_date)}</div>
                        <div><strong>Technical Score:</strong> {bid.technical_score || 'N/A'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem 2rem',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>🔍</div>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>No Bids to Evaluate</h4>
                  <p style={{ margin: 0, fontSize: '1rem' }}>
                    Bids will appear here when vendors submit them for active tenders.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Work Orders Tab */}
          {activeTab === 'work-orders' && (
            <div>
              <h3 style={{ margin: '0 0 2rem 0', color: '#333', fontSize: '1.5rem' }}>
                📄 Work Orders ({workOrders.length})
              </h3>

              {workOrders.length > 0 ? (
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {workOrders.map((order) => (
                    <div key={order.work_order_id} style={{
                      background: '#ffffff',
                      border: '1px solid #e9ecef',
                      borderRadius: '12px',
                      padding: '2rem',
                      transition: 'all 0.3s ease'
                    }}>
                      <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>
                        {order.title}
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '0.5rem', fontSize: '0.9rem' }}>
                        <div><strong>Work Order ID:</strong> {order.work_order_id}</div>
                        <div><strong>Vendor:</strong> {order.vendor_id}</div>
                        <div><strong>Value:</strong> {formatCurrency(order.order_value)}</div>
                        <div><strong>Status:</strong> <span style={{ color: getStatusColor(order.order_status) }}>{order.order_status}</span></div>
                        <div><strong>Order Date:</strong> {formatDate(order.order_date)}</div>
                        <div><strong>Delivery Date:</strong> {formatDate(order.delivery_date)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '4rem 2rem',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>📄</div>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem' }}>No Work Orders</h4>
                  <p style={{ margin: 0, fontSize: '1rem' }}>
                    Work orders will be generated after successful tender completion and vendor selection.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Analytics & Reports Tab */}
          {activeTab === 'analytics' && (
            <div>
              <h3 style={{ margin: '0 0 2rem 0', color: '#333', fontSize: '1.5rem' }}>
                📈 Analytics & Reports
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                gap: '2rem'
              }}>
                {/* Procurement Summary */}
                <div style={{
                  background: '#ffffff',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid #e9ecef'
                }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>📊 Procurement Summary</h4>
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Total Requisitions:</span>
                      <strong>{pendingRequisitions.length}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Active Tenders:</span>
                      <strong>{activeTenders.length}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Registered Vendors:</span>
                      <strong>{vendors.length}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Total Bids:</span>
                      <strong>{bids.length}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Work Orders:</span>
                      <strong>{workOrders.length}</strong>
                    </div>
                  </div>
                </div>

                {/* Quick Reports */}
                <div style={{
                  background: '#ffffff',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid #e9ecef'
                }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#333' }}>📋 Quick Reports</h4>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {[
                      { label: 'Vendor List Report', data: vendors, type: 'vendors' },
                      { label: 'Tender Status Report', data: activeTenders, type: 'tenders' },
                      { label: 'Requisition Report', data: pendingRequisitions, type: 'requisitions' },
                      { label: 'Procurement Summary', data: [], type: 'summary' }
                    ].map((report, index) => (
                      <button
                        key={index}
                        onClick={() => exportToExcel(report.type, report.data)}
                        style={{
                          background: '#f8f9fa',
                          border: '1px solid #e9ecef',
                          padding: '1rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#e9ecef';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#f8f9fa';
                        }}
                      >
                        <span>{report.label}</span>
                        <span style={{ color: '#238548', fontWeight: 'bold' }}>📊 Export</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>


      {/* Modals */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ margin: 0, color: '#333', fontSize: '1.3rem' }}>
                {modalType === 'approve' && '✅ Final Approval'}
                {modalType === 'reject' && '❌ Rejection'}
                {modalType === 'register-vendor' && '🏢 Register New Vendor'}
                {modalType === 'create-tender' && '📋 Create New Tender'}
                {modalType === 'assign-vendor' && '👥 Assign Vendors to Tender'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedRequisition(null);
                  setRejectionReason('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '0.25rem',
                  borderRadius: '4px'
                }}
              >
                ✕
              </button>
            </div>

            {/* Approval Modal */}
            {modalType === 'approve' && selectedRequisition && (
              <div>
                <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                  You are about to give final approval for the requisition "{selectedRequisition.title}".
                  This will authorize the procurement and allow tender creation.
                </p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedRequisition(null);
                    }}
                    style={{
                      background: '#6c757d',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleFinalApproval(selectedRequisition.requisition_id, 'approve')}
                    disabled={loading}
                    style={{
                      background: loading ? '#6c757d' : '#28a745',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Processing...' : '✅ Final Approve'}
                  </button>
                </div>
              </div>
            )}

            {/* Rejection Modal */}
            {modalType === 'reject' && selectedRequisition && (
              <div>
                <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                  Please provide a reason for rejecting "{selectedRequisition.title}":
                </p>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter rejection reason..."
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    marginBottom: '1.5rem',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setSelectedRequisition(null);
                      setRejectionReason('');
                    }}
                    style={{
                      background: '#6c757d',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleFinalApproval(selectedRequisition.requisition_id, 'reject', rejectionReason);
                    }}
                    disabled={loading || !rejectionReason.trim()}
                    style={{
                      background: loading || !rejectionReason.trim() ? '#6c757d' : '#dc3545',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: loading || !rejectionReason.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Processing...' : '❌ Reject'}
                  </button>
                </div>
              </div>
            )}

            {/* Vendor Registration Modal */}
            {modalType === 'register-vendor' && (
              <div>
                <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                  Register a new vendor in the procurement system:
                </p>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                    <input
                      type="text"
                      placeholder="Vendor Name *"
                      value={newVendor.vendor_name}
                      onChange={(e) => setNewVendor({ ...newVendor, vendor_name: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Contact Person *"
                      value={newVendor.contact_person}
                      onChange={(e) => setNewVendor({ ...newVendor, contact_person: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                    <input
                      type="email"
                      placeholder="Email *"
                      value={newVendor.email}
                      onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                    <input
                      type="tel"
                      placeholder="Phone *"
                      value={newVendor.phone}
                      onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Address *"
                    value={newVendor.address_line1}
                    onChange={(e) => setNewVendor({ ...newVendor, address_line1: e.target.value })}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '1rem' }}>
                    <input
                      type="text"
                      placeholder="City *"
                      value={newVendor.city}
                      onChange={(e) => setNewVendor({ ...newVendor, city: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="State *"
                      value={newVendor.state}
                      onChange={(e) => setNewVendor({ ...newVendor, state: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Pincode *"
                      value={newVendor.pincode}
                      onChange={(e) => setNewVendor({ ...newVendor, pincode: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                    <input
                      type="text"
                      placeholder="GST Number"
                      value={newVendor.gst_number}
                      onChange={(e) => setNewVendor({ ...newVendor, gst_number: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="PAN Number"
                      value={newVendor.pan_number}
                      onChange={(e) => setNewVendor({ ...newVendor, pan_number: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <select
                    value={newVendor.company_type}
                    onChange={(e) => setNewVendor({ ...newVendor, company_type: e.target.value })}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="private_limited">Private Limited</option>
                    <option value="public_limited">Public Limited</option>
                    <option value="partnership">Partnership</option>
                    <option value="proprietorship">Proprietorship</option>
                    <option value="government">Government</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setNewVendor({
                        vendor_name: '',
                        contact_person: '',
                        email: '',
                        phone: '',
                        address_line1: '',
                        city: '',
                        state: '',
                        pincode: '',
                        gst_number: '',
                        pan_number: '',
                        company_type: 'private_limited',
                        specialization_categories: [],
                        performance_rating: 0
                      });
                    }}
                    style={{
                      background: '#6c757d',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleVendorRegistration}
                    disabled={loading}
                    style={{
                      background: loading ? '#6c757d' : '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Registering...' : '🏢 Register Vendor'}
                  </button>
                </div>
              </div>
            )}

            {/* Tender Creation Modal */}
            {modalType === 'create-tender' && (
              <div>
                <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                  Create a new tender for procurement:
                </p>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <input
                    type="text"
                    placeholder="Tender Title *"
                    value={newTender.title}
                    onChange={(e) => setNewTender({ ...newTender, title: e.target.value })}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                  <textarea
                    placeholder="Description *"
                    value={newTender.description}
                    onChange={(e) => setNewTender({ ...newTender, description: e.target.value })}
                    rows="3"
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                    <input
                      type="number"
                      placeholder="Estimated Value *"
                      value={newTender.estimated_value}
                      onChange={(e) => setNewTender({ ...newTender, estimated_value: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Department *"
                      value={newTender.department}
                      onChange={(e) => setNewTender({ ...newTender, department: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                    <select
                      value={newTender.procurement_category}
                      onChange={(e) => setNewTender({ ...newTender, procurement_category: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat.category_code} value={cat.category_code}>
                          {cat.category_icon} {cat.category_name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={newTender.tender_type}
                      onChange={(e) => setNewTender({ ...newTender, tender_type: e.target.value })}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="open">Open Tender</option>
                      <option value="limited">Limited Tender</option>
                      <option value="single_source">Single Source</option>
                      <option value="emergency">Emergency</option>
                    </select>
                  </div>
                  <input
                    type="datetime-local"
                    placeholder="Submission Deadline"
                    value={newTender.submission_deadline}
                    onChange={(e) => setNewTender({ ...newTender, submission_deadline: e.target.value })}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setNewTender({
                        title: '',
                        description: '',
                        estimated_value: '',
                        department: '',
                        procurement_category: '',
                        submission_deadline: '',
                        tender_type: 'open'
                      });
                    }}
                    style={{
                      background: '#6c757d',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTenderCreation}
                    disabled={loading}
                    style={{
                      background: loading ? '#6c757d' : '#238548',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Creating...' : '📋 Create Tender'}
                  </button>
                </div>
              </div>
            )}

            {/* Vendor Assignment Modal */}
            {modalType === 'assign-vendor' && (
              <div>
                <p style={{ marginBottom: '1.5rem', color: '#666' }}>
                  Assign vendors to tender for quotation submission:
                </p>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <select
                    value={vendorAssignment.tender_id}
                    onChange={(e) => setVendorAssignment({ ...vendorAssignment, tender_id: e.target.value })}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="">Select Tender *</option>
                    {activeTenders.map(tender => (
                      <option key={tender.tender_id} value={tender.tender_id}>
                        {tender.title} - {tender.tender_id}
                      </option>
                    ))}
                  </select>

                  <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1rem' }}>
                    <h5 style={{ margin: '0 0 1rem 0', color: '#333' }}>Select Vendors (Multiple selection allowed):</h5>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                      gap: '0.5rem',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {vendors.filter(vendor => vendor.vendor_status !== 'blacklisted').map(vendor => (
                        <label key={vendor.vendor_id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem',
                          border: '1px solid #e9ecef',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}>
                          <input
                            type="checkbox"
                            checked={vendorAssignment.vendor_ids.includes(vendor.vendor_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setVendorAssignment({
                                  ...vendorAssignment,
                                  vendor_ids: [...vendorAssignment.vendor_ids, vendor.vendor_id]
                                });
                              } else {
                                setVendorAssignment({
                                  ...vendorAssignment,
                                  vendor_ids: vendorAssignment.vendor_ids.filter(id => id !== vendor.vendor_id)
                                });
                              }
                            }}
                            style={{ marginRight: '0.5rem' }}
                          />
                          <div>
                            <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{vendor.vendor_name}</div>
                            <div style={{ color: '#666', fontSize: '0.8rem' }}>{vendor.contact_person}</div>
                            <div style={{ color: '#666', fontSize: '0.75rem' }}>⭐ {vendor.performance_rating || 0}/5</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <input
                    type="datetime-local"
                    placeholder="Assignment Date"
                    value={vendorAssignment.assignment_date}
                    onChange={(e) => setVendorAssignment({ ...vendorAssignment, assignment_date: e.target.value })}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                  />

                  <textarea
                    placeholder="Assignment Notes (Optional)"
                    value={vendorAssignment.notes}
                    onChange={(e) => setVendorAssignment({ ...vendorAssignment, notes: e.target.value })}
                    rows="3"
                    style={{
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      setVendorAssignment({
                        tender_id: '',
                        vendor_ids: [],
                        assignment_date: '',
                        notes: ''
                      });
                    }}
                    style={{
                      background: '#6c757d',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleVendorAssignment}
                    disabled={loading || !vendorAssignment.tender_id || vendorAssignment.vendor_ids.length === 0}
                    style={{
                      background: loading || !vendorAssignment.tender_id || vendorAssignment.vendor_ids.length === 0 ? '#6c757d' : '#6f42c1',
                      color: '#ffffff',
                      border: 'none',
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      cursor: loading || !vendorAssignment.tender_id || vendorAssignment.vendor_ids.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? 'Assigning...' : '👥 Assign Vendors'}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#dc3545',
          color: '#ffffff',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          zIndex: 2001,
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          maxWidth: '400px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>❌ {error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#ffffff',
                fontSize: '1.2rem',
                cursor: 'pointer',
                marginLeft: '1rem'
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminRequisition;